import { RWSService } from "@rws-framework/client";

import type {
  DashboardResponse,
  Course as CourseRaw,
  CoursesDetailResponse,
  CreateProgramResponse,
  EditCourseResponse,
  EditCoursePayload,
  Professor,
  CourseClass,
  CreateBlankCoursePayload,
  CourseListItem,
  Tag,
} from "./typesRaw";

/**
 * Wraps direct interactions with backend. Used by CourseService.
 */
class CourseApiService extends RWSService {
  fetchCourses(): Promise<{
    courses: CourseRaw[];
    catalog: CourseRaw[];
    archived: CourseRaw[];
    assistants_and_instructors: Professor[];
    classes: CourseClass[];
  }> {
    return fetch("/server/courses/").then(CourseApiService.formatResponse);
  }

  fetchCatalogCourses(): Promise<CourseListItem[]> {
    return fetch("/server/course_list/catalogs").then(
      CourseApiService.formatResponse,
    );
  }

  fetchCoursesToCopy(): Promise<CourseListItem[]> {
    return fetch("/server/course_list/existing").then(
      CourseApiService.formatResponse,
    );
  }

  fetchArchivedCourses(): Promise<{
    archived: CourseRaw[];
    assistants_and_instructors: Professor[];
    classes: CourseClass[];
  }> {
    return fetch("/server/courses/archived/").then(
      CourseApiService.formatResponse,
    );
  }

  fetchCourse(courseId: string): Promise<CoursesDetailResponse> {
    return fetch(`/server/courses/${courseId}`).then(
      CourseApiService.formatResponse,
    );
  }

  createProgramCourse(
    payload: Record<string, unknown>,
  ): Promise<CreateProgramResponse> {
    return fetch("/server/section_management/make_program", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(payload),
    }).then(CourseApiService.formatResponse);
  }

  createBlankCourse(
    payload: CreateBlankCoursePayload = {},
  ): Promise<{ new_id: string }> {
    return fetch("/server/collection/courses/new/", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(payload),
    }).then(CourseApiService.formatResponse);
  }

  editCourse(
    courseId: string,
    payload: Partial<EditCoursePayload>,
  ): Promise<EditCourseResponse> {
    return fetch(`/server/section_management/edit/${courseId}`, {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(payload),
    }).then(CourseApiService.formatResponse);
  }

  createSectionOrCalendarDuplicate(
    sectionOrCalendarId: string,
  ): Promise<{ id: string; slug: string }> {
    return fetch(
      `/server/section_management/duplicate/${sectionOrCalendarId}`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({}),
      },
    ).then(CourseApiService.formatResponse);
  }

  duplicateRawCourse(
    courseId: string,
    payload: CreateBlankCoursePayload = {},
  ): Promise<{ new_id: string; slug: string }> {
    return fetch(`/server/clone/courses/${courseId}/`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(payload),
    }).then(CourseApiService.formatResponse);
  }

  createCalendar(
    courseId: string,
    payload: Partial<{ title: string }>,
  ): Promise<CreateProgramResponse> {
    return fetch(`/server/section_management/section/${courseId}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(payload),
    }).then(CourseApiService.formatResponse);
  }

  createRawSection(
    courseId: string,
    calendarId: string,
    instructorId: string,
    payload: Partial<{ title: string }>,
  ): Promise<CreateProgramResponse> {
    return fetch(
      `/server/section_management/section/${courseId}/${calendarId}/${instructorId}`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({ ...payload, assistantOnly: false }),
      },
    ).then(CourseApiService.formatResponse);
  }

  editRawCourse(
    calendarId: string,
    props: Partial<CourseRaw>,
  ): Promise<{ course_id: string }> {
    return fetch(`/server/collection/courses/${calendarId}/`, {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(props),
    }).then(CourseApiService.formatResponse);
  }

  /**
   *
   * @param courseId master id
   * @param slug slug of section
   * @param instructorId
   * @returns
   */
  changeSectionInstructor(
    courseId: string,
    slug: string,
    instructorId: string,
  ): Promise<CreateProgramResponse> {
    return fetch(
      `/server/section_management/replace_instructor/${courseId}/${slug}/${instructorId}/1`,
    ).then(CourseApiService.formatResponse);
  }

  /** sets `enrollment_locked` and `status: 'archived'` on course */
  archiveCourse(courseId: string): Promise<unknown> {
    return fetch(`/server/courses/${courseId}/archive`, {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({}),
    }).then(CourseApiService.formatResponse);
  }

  /** unsets `enrollment_locked` and sets `status: 'active'` on course */
  unarchiveCourse(courseId: string): Promise<unknown> {
    return fetch(`/server/courses/${courseId}/restore`, {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({}),
    }).then(CourseApiService.formatResponse);
  }

  /**
   * marks the course as archived ONLY for the current instructor
   * (by moving their id from `instructors`/`assistants` to `archived`)
   * this only hides the course visually, does not affect enrollments etc.
   */
  hideCourse(courseId: string): Promise<unknown> {
    return fetch(`/server/collection/courses/${courseId}/`, {
      method: "DELETE",
    }).then(CourseApiService.formatResponse);
  }

  /**
   * reverses hiding the course for particular instructor
   * (moves instructor out of `archived` list and back to `instructors`/`assistants`)
   */
  unhideCourse(courseId: string): Promise<unknown> {
    return fetch(`/server/restore_course/${courseId}`, {
      method: "GET",
    }).then(CourseApiService.formatResponse);
  }

  /** sets `enrollment_locked` and `suppressed`, clears list of instructors */
  archiveCalendar(calendarId: string): Promise<unknown> {
    return fetch(`/server/section_management/unhook/${calendarId}`, {
      method: "PUT",
    }).then(CourseApiService.formatResponse);
  }

  /** sets `enrollment_locked` and `suppressed`, clears list of instructors */
  archiveSection(sectionId: string): Promise<unknown> {
    return fetch(`/server/section_management/unhook/${sectionId}`, {
      method: "PUT",
    }).then(CourseApiService.formatResponse);
  }

  renameCalendar(calendarId: string, title: string): Promise<unknown> {
    return fetch(`/server/section_management/edit/${calendarId}`, {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({ title }),
    }).then(CourseApiService.formatResponse);
  }

  renameSection(sectionId: string, title: string): Promise<unknown> {
    return fetch(`/server/section_management/edit/${sectionId}`, {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({ title }),
    }).then(CourseApiService.formatResponse);
  }

  moveSectionToCalendar(
    courseId: string,
    sectionId: string,
    calendarId: string,
  ): Promise<unknown> {
    return fetch(`/server/section_management/move_section/${courseId}/${sectionId}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({ to_section_template: calendarId }),
    }).then(CourseApiService.formatResponse);
  }

  editCourseFeatures(
    courseId: string,
    features: Record<string, unknown>,
  ): Promise<unknown> {
    return fetch(`/server/collection/courses/${courseId}`, {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(features),
    }).then(CourseApiService.formatResponse);
  }

  createClass(name: string): Promise<unknown> {
    return fetch(`/server/course-classes/`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({ name }),
    }).then(CourseApiService.formatResponse);
  }

  fetchTags(): Promise<Tag[]> {
    return fetch("/server/tags").then(CourseApiService.formatResponse);
  }

  static formatResponse = <T = any>(res: Response): Promise<T> => {
    const isJsonContent =
      res.headers.get("content-type") === "application/json";

    if (res.ok && isJsonContent)
      return res.json().then((body) => {
        // some endpoints return status 200 with error payload...
        if (
          body &&
          typeof body === "object" &&
          ("errno" in body || "error" in body)
        )
          throw new CourseApiError(res, body.errno);

        return body;
      });

    if (res.ok) return Promise.resolve({} as T);

    if (isJsonContent)
      return res.json().then((errBody) => {
        if (!errBody || typeof errBody !== "object")
          throw new CourseApiError(res);

        throw "code" in errBody
          ? new CourseApiError(res, errBody.code)
          : new CourseApiError(res);
      });

    return Promise.reject(new CourseApiError(res));
  };
}

export default CourseApiService.getSingleton();

export { CourseApiService };

export class CourseApiError extends Error {
  constructor(
    res: Response,
    public code: string = "unknown",
  ) {
    super(res.statusText);
  }

  get humanMessage(): string | null {
    switch (this.code) {
      case "unhook-not-found":
        return "Removed section or calendar not found";

      case "unhook-section-template-in-use":
        return "Cannot remove calendar containing sections";

      case "unhook-section-with-students":
        return "Cannot remove section with students enrolled";

      case "access-denied":
        return "Your account has insufficient privileges";

      default:
        return null;
    }
  }
}
